<template>
  <AppCard
    tag="article"
    :design="design"
    :padding="padding"
    class="app-metric-card"
    :class="[`app-metric-card--${tone}`, { 'app-metric-card--new': design === 'new' }]"
  >
    <div class="app-metric-card__header">
      <div class="app-metric-card__label">{{ label }}</div>
      <span
        v-if="status || help"
        v-tooltip="{
          content: status || help,
          placement: 'left',
          offset: 8,
          classes: 'tooltip--new'
        }"
        class="app-metric-card__info"
        :class="status ? `app-metric-card__info--${statusTone}` : null"
        tabindex="0"
        :aria-label="status ? `${label}: ${status}` : `About ${label}`"
      >{{ status ? '!' : 'i' }}</span>
    </div>
    <div
      class="app-metric-card__content"
      :class="{ 'app-metric-card__content--with-actions': $slots.actions }"
    >
      <div class="app-metric-card__copy">
        <div class="app-metric-card__value">
          <slot>{{ value }}</slot>
        </div>
        <div v-if="meta || $slots.meta" class="app-metric-card__meta">
          <slot name="meta">{{ meta }}</slot>
        </div>
      </div>
      <div v-if="$slots.actions" class="app-metric-card__actions">
        <slot name="actions" />
      </div>
    </div>
  </AppCard>
</template>

<script>
import AppCard from '@/components/AppCard'

export default {
  name: 'AppMetricCard',
  components: {AppCard},
  props: {
    label: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number],
      default: ''
    },
    meta: {
      type: String,
      default: ''
    },
    tone: {
      type: String,
      default: 'neutral',
      validator: value => ['neutral', 'blue', 'green', 'amber', 'red'].includes(value)
    },
    help: {
      type: String,
      default: ''
    },
    status: {
      type: String,
      default: ''
    },
    statusTone: {
      type: String,
      default: 'red',
      validator: value => ['blue', 'green', 'amber', 'red'].includes(value)
    },
    design: {
      type: String,
      required: true,
      validator: value => value === 'new'
    },
    padding: {
      type: String,
      default: 'normal',
      validator: value => ['none', 'compact', 'normal'].includes(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.app-metric-card--new {
  position: relative;
  display: flex;
  min-height: 104px;
  flex-direction: column;
  overflow: hidden;

  &::before {
    position: absolute;
    top: $ui-space-20;
    bottom: $ui-space-20;
    left: 0;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background-color: $ui-line-strong;
    content: '';
  }

  &.app-metric-card--blue::before { background-color: $ui-blue; }
  &.app-metric-card--green::before { background-color: $ui-green; }
  &.app-metric-card--red::before { background-color: $ui-red; }
  &.app-metric-card--amber::before { background-color: $ui-amber; }
}

.app-metric-card__header,
.app-metric-card__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $ui-space-8;
}

.app-metric-card__label {
  color: $ui-muted;
  font-size: $ui-font-size-body;
  font-weight: 600;
  line-height: 18px;
}

.app-metric-card__info {
  display: inline-flex;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  border: 1px solid $ui-line-strong;
  border-radius: $ui-control-radius;
  background-color: $ui-surface-soft;
  color: $ui-blue;
  font-family: $ui-font-heading;
  font-weight: 600;

  &:focus-visible {
    outline: 3px solid rgba($ui-blue, .18);
    outline-offset: 2px;
  }

  &.app-metric-card__info--green { color: $ui-green; }
  &.app-metric-card__info--red { color: $ui-red; }
  &.app-metric-card__info--amber { color: $ui-amber; }
}

.app-metric-card__content {
  flex: 1;
  align-items: flex-end;
  gap: $ui-space-12;
}

.app-metric-card__content--with-actions {
  align-items: stretch;
  flex-direction: column;
}

.app-metric-card__copy {
  min-width: 0;
}

.app-metric-card__value {
  margin: $ui-space-8 0 $ui-space-4;
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: $ui-font-size-page-title;
  font-weight: 600;
  line-height: 1.05;
}

.app-metric-card--red .app-metric-card__value { color: $ui-red; }
.app-metric-card--green .app-metric-card__value { color: $ui-green; }

.app-metric-card__meta {
  color: $ui-muted;
  font-size: $ui-font-size-body;
  line-height: 18px;
}

.app-metric-card__actions {
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-start;
  padding-top: $ui-space-8;
}
</style>
